"use client";

import { useEffect, useRef, useState, Suspense } from "react";
import Image from "next/image";
import { gsap } from "gsap";
// import { button } from "framer-motion/client";

// ✅ Skeleton Component
function HeroSkeleton() {
  return (
    <section className="relative overflow-hidden min-h-screen flex flex-col md:flex-row justify-between items-center px-8 md:px-20 py-16  animate-pulse">
      {/* Left Section */}
      <div className="flex-1 flex justify-center items-center">
        <div className="w-80 h-80 bg-gray-300 rounded-2xl" />
      </div>

      {/* Right Section */}
      <div className="flex-1 flex flex-col gap-4">
        <div className="w-48 h-6 bg-gray-300 rounded"></div>
        <div className="w-72 h-10 bg-gray-300 rounded"></div>
        <div className="w-64 h-8 bg-gray-300 rounded"></div>
        <div className="w-96 h-5 bg-gray-300 rounded mt-4"></div>

        <div className="mt-6 space-y-3">
          {Array(4)
            .fill(0)
            .map((_, i) => (
              <div
                key={i}
                className="w-[300px] h-8 bg-gray-300 rounded-full"
              ></div>
            ))}
        </div>
      </div>
    </section>
  );
}

// content + images used by hero (keep the same design, only content swaps)
const HERO_SLIDES = [
  {
    image: "/5.png",
    brand: "PARA INNOVATION",
    titleMain: "KISAN MITHRAA",
    titleSub: "MOBILE STARTER",
    description:
      "Mobile Starter : Effortless Motor Control at Your Fingertips By Using Your Mobile",
    location: "Hyderabad",
    buttonText: "Order Now",
    features: [
      "DRY RUN PROTECTION",
      "OVER LOAD PROTECTION",
      "CALL / SMS CONTROL",
      "24/7 CUSTOMER SUPPORT",
    ],

    styles: {
      brand: "text-[#4c4444] font-bold",
      titleMain: "text-[#6BAF92] font-extrabold italic",
      titleSub: "text-[#66CDAA] font-semibold",
      description: "text-gray-700 font-medium italic",
    },
  },
  {
    image: "/remote_1.png",
    brand: "PARA INNOVATION",
    titleMain: "KISAN MITHRAA",
    titleSub: "REMOTE STARTER",
    description:
      "Remote Starter: Reliable motor start from distance with physical remote",
    location: "Hyderabad",
    buttonText: "Order Now",
    features: [
      "upto 2KM Range",
      "Easy Installation",
      "Battery Backup Included",
      "12 months warranty",
      "Inbuilt Automatic Starter",
    ],

    styles: {
      brand: "text-[#4c4444] font-bold",
      titleMain: "text-[#6BAF92] font-extrabold italic",
      titleSub: "text-[#66CDAA] font-semibold ",
      description: "text-gray-700 font-medium italic",
    },
  },
];

// ✅ Actual Hero Section with auto-slide every 5 seconds
function HeroContent() {
  const heroRef = useRef<HTMLElement | null>(null);
  const leftSectionRef = useRef<HTMLDivElement | null>(null);
  const rightSectionRef = useRef<HTMLDivElement | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef<number | null>(null);

  // Preload all images
  useEffect(() => {
    const images = [
      "/hmpg.jpg",
      "/Para_logo.webp",
      "/100g.png",
      ...HERO_SLIDES.map((s) => s.image),
      "/stage.png",
    ];

    Promise.all(
      images.map(
        (src) =>
          new Promise((resolve) => {
            const img = new window.Image();
            img.src = src;
            img.onload = () => resolve(true);
            img.onerror = () => resolve(true);
          }),
      ),
    ).then(() => setLoaded(true));
  }, []);

  // Entrance animation (keeps existing animation behaviour)
  useEffect(() => {
    if (!loaded) return;
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
      tl.from(".hero-left", { x: -100, opacity: 0, duration: 1 })
        .from(".hero-right", { x: 100, opacity: 0, duration: 1 }, "-=0.6")
        .from(
          ".feature",
          { y: 40, opacity: 0, duration: 0.8, stagger: 0.15 },
          "-=0.4",
        );
    }, heroRef);

    return () => ctx.revert();
  }, [loaded]);

  // Auto-advance every 5 seconds
  useEffect(() => {
    if (!loaded) return;

    intervalRef.current = window.setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % HERO_SLIDES.length);
    }, 10000);

    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
      }
    };
  }, [loaded]);

  // Animate slide transitions
  useEffect(() => {
    if (!loaded || !leftSectionRef.current || !rightSectionRef.current) return;

    const tl = gsap.timeline();

    // Slide out to left
    tl.to([leftSectionRef.current, rightSectionRef.current], {
      x: -100,
      opacity: 0,
      duration: 0.5,
      ease: "power2.in",
    })
      // Reset position off-screen to the right
      .set([leftSectionRef.current, rightSectionRef.current], {
        x: 100,
        opacity: 0,
      })
      // Slide in from right
      .to([leftSectionRef.current, rightSectionRef.current], {
        x: 0,
        opacity: 1,
        duration: 0.6,
        ease: "power3.out",
      });

    return () => {
      tl.kill();
    };
  }, [currentIndex, loaded]);

  if (!loaded) return <HeroSkeleton />;

  const slide = HERO_SLIDES[currentIndex];

  return (
    <section
      ref={heroRef}
      className="relative overflow-hidden min-h-screen  flex flex-col md:flex-row justify-between items-center px-8 md:px-20 py-16 "
    >
      {/* ✅ Background Image */}
      <div className="absolute bg-white inset-0 -z-10">
        <Image
          src="/bg-v-k.jpg"
          alt="Background"
          fill
          priority
          className="object-cover w-full  w-200  lg:object-contain object-bottom transition-opacity duration-700"
        />
      </div>

      {/* ✅ Decorative Wrapper */}
      <div className="absolute hidden lg:block inset-0 z-0 overflow-hidden">
        <Image
          src="/Para_logo.webp"
          alt="Para Logo"
          width={250}
          height={250}
          className=" absolute   top-0 left-0   object-contain opacity-90"
          loading="eager"
        />
        <Image
          src="/100g.png"
          alt="Guarantee Seal"
          width={180}
          height={180}
          className="absolute  right-1 top-2  object-contain"
          loading="eager"
        />
      </div>

      {/* ✅ Left Product Section */}
      <div
        ref={leftSectionRef}
        className="hero-left relative flex-1 flex justify-center items-center z-10"
      >
        <div className="absolute -left-10 top-1/2 -rotate-90 text-3xl tracking-widest lg:text-gray-600 font-semibold select-none"></div>

        <div className="relative z-10 flex items-center justify-center w-full max-w-md md:max-w-lg lg:max-w-xl">
          <div
            className="flex items-center justify-center"
            style={{ padding: "1rem" }}
          >
            <Image
              src={slide.image}
              alt={`${slide.titleSub} image`}
              width={320}
              height={320}
              className="rounded-2xl object-contain"
              loading="eager"
            />
          </div>
        </div>

        <Image
          src="/stage.png"
          alt="Stage"
          width={520}
          height={520}
          className="absolute mb-20 lg:mb-[20] -bottom-30 w-96 object-contain"
          loading="eager"
        />
      </div>

      {/* ✅ Right Info Section */}
      <div
        ref={rightSectionRef}
        className="hero-right lg:mt-0 mt-10 flex-1 flex flex-col items-start gap-4 text-gray-800 z-10"
      >
        <h2
          className={`text-2xl tracking-wider ${slide.styles.brand} text-white md:text-black lg:text-black lg:bg-transparent backdrop-blur-sm px-4 py-2 rounded-lg`}
        >
          {slide.brand}
        </h2>

        <h1
          className={`text-6xl tracking-wide ${slide.styles.titleMain}  md:text-gray lg:text-gray lg:bg-transparent bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg`}
        >
          {slide.titleMain}
        </h1>

        <h1
          className={`text-3xl mb-4 ${slide.styles.titleSub} text-white md:text-[#6BAF92] lg:text-[#6BAF92] lg:bg-transparent bg-white/40 backdrop-blur-sm px-4 py-2 rounded-lg`}
        >
          {slide.titleSub}
        </h1>

        <p
          className={`text-lg ${slide.styles.description} text-black md:text-gray lg:text-gray lg:bg-transparent bg-white/40 backdrop-blur-sm px-4 py-2 rounded-lg`}
        >
          {slide.description}
        </p>

        <div className="mt-6 space-y-3">
          {slide.features.map((text, i) => (
            <div
              key={i}
              className="feature flex justify-between items-center bg-[#e5e7e4] px-4 py-2 rounded-full text-sm font-medium shadow-sm border border-gray-200 w-[300px]"
            >
              <span>{text}</span>
              <span className="text-gray-400 font-extrabold tracking-widest">
                •••
              </span>
            </div>
          ))}
          <button className="px-10 py-4 mt-3 bg-[#6BAF92]  hover:bg-[#5EA785] text-white text-lg font-medium rounded-full  transition-all duration-300 hover:shadow-lg hover:scale-105  cursor-pointer">
            <a href="/Order">{slide.buttonText}</a>
          </button>
        </div>
      </div>
    </section>
  );
}

// ✅ Suspense Wrapper
export default function HeroSection() {
  return (
    <Suspense fallback={<HeroSkeleton />}>
      <HeroContent />
    </Suspense>
  );
}
